import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBizCardUpload } from '@hooks';

const PROCESS_STEPS = [
    { key: 'uploading',  label: () => '이미지 업로드' },
    { key: 'processing', label: (llm) => llm ? 'OCR + AI 분석' : 'OCR 분석' },
    { key: 'done',       label: () => '분석 완료' },
];

function getStepState(currentStep, stepKey) {
    const order = ['uploading', 'processing', 'done'];
    const ci = order.indexOf(currentStep);
    const si = order.indexOf(stepKey);
    if (!['uploading', 'processing', 'done'].includes(currentStep)) return 'waiting';
    if (si < ci)  return 'done';
    if (si === ci) return 'active';
    return 'waiting';
}

function getPolygonPoints(points) {
    return points.map((p) => `${p.x},${p.y}`).join(' ');
}

// SVG crop overlay on top of the image
function CropOverlaySvg({ cropOverlay, imageNaturalSize }) {
    if (!cropOverlay?.points || cropOverlay.points.length !== 4) return null;
    if (!imageNaturalSize.width || !imageNaturalSize.height) return null;
    return (
        <svg
            viewBox={`0 0 ${imageNaturalSize.width} ${imageNaturalSize.height}`}
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
            <polygon
                points={getPolygonPoints(cropOverlay.points)}
                fill="rgba(78, 115, 223, 0.10)"
                stroke="rgba(78, 115, 223, 0.85)"
                strokeWidth="8"
                vectorEffect="non-scaling-stroke"
            />
            {cropOverlay.points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="16" fill="rgba(78,115,223,0.9)" vectorEffect="non-scaling-stroke" />
            ))}
        </svg>
    );
}

const CARD_STYLE = {
    background: '#fff',
    border: '1.5px solid #e2e5ea',
    borderRadius: '10px',
    padding: '18px',
};

const LABEL_STYLE = {
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    marginBottom: '12px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
};

const SPINNER_STYLE = {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #4e73df',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'upload-spin 0.8s linear infinite',
    flexShrink: 0,
};

export function BizCardUpload() {
    const navigate       = useNavigate();
    const fileInputRef   = useRef(null);
    const cameraInputRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    const {
        selectedFile, previewUrl, imageNaturalSize, setImageNaturalSize,
        step, stepMessage, errorMessage,
        useLlm, setUseLlm,
        cropOverlay, cropPreviewUrl, cropStatusText,
        handleFileChange, handleDrop, handleDragOver, handleUploadAndProcess,
    } = useBizCardUpload();

    const isDetecting  = step === 'detecting';
    const isProcessing = step === 'uploading' || step === 'processing';
    const isBusy       = isDetecting || isProcessing;
    const showProgress = isProcessing || step === 'done';
    const showCropArea = cropPreviewUrl || isDetecting;

    const onFileInputChange = (e) => {
        handleFileChange(e.target.files[0]);
        e.target.value = '';
    };

    return (
        <div className="contents-wrap">
            <div className="page-title-wrap">
                <h2>
                    <span className="icon-dot-circle" />
                    명함 업로드
                </h2>
            </div>

            <section>
                <div className="section-tit-wrap">
                    <h3 className="border-left-primary">&nbsp;이미지 등록</h3>
                    <div className="btn-right-box">
                        <ul>
                            <li>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => navigate('/biz/card-list')}
                                >
                                    목록으로
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: '20px', alignItems: 'start' }}>

                    {/* ───────────────── LEFT: 이미지 패널 ───────────────── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                        {/* 원본 이미지 */}
                        <div style={CARD_STYLE}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <p style={{ ...LABEL_STYLE, marginBottom: 0 }}>원본 이미지</p>
                                {selectedFile && !isBusy && (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ background: 'none', border: 'none', fontSize: '12px', color: '#4e73df', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                    >
                                        다른 이미지 선택
                                    </button>
                                )}
                            </div>

                            {!previewUrl ? (
                                /* Drop Zone */
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => !isBusy && fileInputRef.current?.click()}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                                    style={{
                                        border: '2px dashed #c8cdd8',
                                        borderRadius: '8px',
                                        padding: '60px 24px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: '#fafbfc',
                                        transition: 'all 0.18s',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#4e73df';
                                        e.currentTarget.style.background  = '#f0f4ff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#c8cdd8';
                                        e.currentTarget.style.background  = '#fafbfc';
                                    }}
                                >
                                    <div style={{ fontSize: '48px', marginBottom: '14px', lineHeight: 1 }}>📇</div>
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 6px' }}>
                                        이미지를 끌어다 놓거나 클릭하세요
                                    </p>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>JPG · PNG · WEBP 지원</p>
                                </div>
                            ) : (
                                /* 이미지 미리보기 + crop overlay */
                                <div style={{ position: 'relative', lineHeight: 0, borderRadius: '6px', overflow: 'hidden' }}>
                                    <img
                                        src={previewUrl}
                                        alt="명함 미리보기"
                                        onLoad={(e) => setImageNaturalSize({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
                                        draggable={false}
                                        style={{ width: '100%', maxHeight: '340px', objectFit: 'contain', display: 'block', background: '#f8f9fa' }}
                                    />
                                    <CropOverlaySvg cropOverlay={cropOverlay} imageNaturalSize={imageNaturalSize} />
                                    {isDetecting && (
                                        <div style={{
                                            position: 'absolute', inset: 0,
                                            background: 'rgba(255,255,255,0.75)',
                                            display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', justifyContent: 'center',
                                            gap: '10px', fontSize: '13px', color: '#475569',
                                        }}>
                                            <div style={SPINNER_STYLE} />
                                            명함 영역 탐지 중...
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedFile && (
                                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px', marginBottom: 0 }}>
                                    {selectedFile.name}
                                </p>
                            )}
                        </div>

                        {/* Crop 미리보기 */}
                        {showCropArea && (
                            <div style={CARD_STYLE}>
                                <p style={{ ...LABEL_STYLE }}>자동 탐지 미리보기</p>
                                {isDetecting ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '13px', padding: '12px 0' }}>
                                        <div style={SPINNER_STYLE} />
                                        명함 영역을 분석하는 중...
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={cropPreviewUrl}
                                            alt="crop 미리보기"
                                            style={{ width: '100%', borderRadius: '6px', objectFit: 'contain', display: 'block', marginBottom: '10px', maxHeight: '200px' }}
                                        />
                                        <p style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', margin: '0 0 4px' }}>{cropStatusText}</p>
                                        {cropOverlay && (
                                            <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>
                                                방법: <strong style={{ color: '#475569' }}>{cropOverlay.method}</strong>
                                                &nbsp;·&nbsp;크기: {cropOverlay.width}×{cropOverlay.height}
                                                &nbsp;·&nbsp;감지: {cropOverlay.found ? '✅' : '❌'}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ───────────────── RIGHT: 옵션 + 진행 + 버튼 ───────────────── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                        {/* 파일 선택 버튼 */}
                        <div style={CARD_STYLE}>
                            <p style={LABEL_STYLE}>이미지 선택</p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        flex: 1,
                                        padding: '9px 12px',
                                        background: '#f0f4ff',
                                        border: '1px solid #c7d7fd',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        color: '#4e73df',
                                        fontWeight: '600',
                                        cursor: isBusy ? 'not-allowed' : 'pointer',
                                        opacity: isBusy ? 0.5 : 1,
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    📁 파일 선택
                                </button>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => cameraInputRef.current?.click()}
                                    style={{
                                        flex: 1,
                                        padding: '9px 12px',
                                        background: '#f0f4ff',
                                        border: '1px solid #c7d7fd',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        color: '#4e73df',
                                        fontWeight: '600',
                                        cursor: isBusy ? 'not-allowed' : 'pointer',
                                        opacity: isBusy ? 0.5 : 1,
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    📸 카메라
                                </button>
                            </div>
                            {selectedFile && (
                                <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f8faff', borderRadius: '5px', fontSize: '12px', color: '#475569' }}>
                                    <strong>선택된 파일:</strong> {selectedFile.name}
                                </div>
                            )}
                        </div>

                        {/* LLM 옵션 */}
                        <div style={CARD_STYLE}>
                            <p style={LABEL_STYLE}>분석 옵션</p>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: isBusy ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={useLlm}
                                    onChange={(e) => setUseLlm(e.target.checked)}
                                    disabled={isBusy}
                                    style={{ width: '16px', height: '16px', marginTop: '2px', accentColor: '#4e73df', flexShrink: 0, cursor: isBusy ? 'not-allowed' : 'pointer' }}
                                />
                                <div>
                                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#334155', margin: '0 0 3px' }}>AI 언어모델 보정 사용</p>
                                    <p style={{ fontSize: '11px', color: useLlm ? '#2563eb' : '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                                        {useLlm
                                            ? 'OCR 결과를 LLM으로 보정합니다. 정확도가 높아지나 1~3분 소요됩니다.'
                                            : 'OCR 결과만 사용합니다. 빠르지만 정확도가 낮을 수 있습니다.'}
                                    </p>
                                </div>
                            </label>
                        </div>

                        {/* 처리 단계 표시 */}
                        {showProgress && (
                            <div style={CARD_STYLE}>
                                <p style={LABEL_STYLE}>처리 상태</p>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {PROCESS_STEPS.map(({ key, label }) => {
                                        const state     = getStepState(step, key);
                                        const labelText = label(useLlm);
                                        return (
                                            <li key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '11px', fontWeight: '700',
                                                    background: state === 'done' ? '#dcfce7' : state === 'active' ? '#dbeafe' : '#f1f5f9',
                                                    color:      state === 'done' ? '#15803d' : state === 'active' ? '#1d4ed8' : '#cbd5e1',
                                                    border: `1.5px solid ${state === 'done' ? '#86efac' : state === 'active' ? '#93c5fd' : '#e2e8f0'}`,
                                                }}>
                                                    {state === 'active'  ? <div style={SPINNER_STYLE} /> : state === 'done' ? '✓' : '○'}
                                                </div>
                                                <span style={{ fontSize: '13px', fontWeight: state === 'active' ? '600' : '400', color: state === 'done' ? '#15803d' : state === 'active' ? '#1e40af' : '#94a3b8' }}>
                                                    {labelText}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {stepMessage && (
                                    <p style={{ fontSize: '11px', color: '#64748b', marginTop: '12px', marginBottom: 0, paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                                        {stepMessage}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* 오류 메시지 */}
                        {step === 'error' && (
                            <div style={{ background: '#fff5f5', border: '1.5px solid #fca5a5', borderRadius: '10px', padding: '14px 16px' }}>
                                <p style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b', margin: '0 0 6px' }}>오류가 발생했습니다</p>
                                <p style={{ fontSize: '12px', color: '#b91c1c', margin: '0 0 10px', wordBreak: 'break-word' }}>{errorMessage}</p>
                                <button
                                    type="button"
                                    onClick={handleUploadAndProcess}
                                    style={{ background: 'none', border: 'none', fontSize: '12px', color: '#4e73df', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                >
                                    다시 시도
                                </button>
                            </div>
                        )}

                        {/* 분석 시작 */}
                        <button
                            type="button"
                            onClick={handleUploadAndProcess}
                            disabled={!selectedFile || isBusy}
                            style={{
                                width: '100%',
                                padding: '13px',
                                background: !selectedFile || isBusy ? '#e2e8f0' : '#4e73df',
                                color:  !selectedFile || isBusy ? '#94a3b8' : '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '700',
                                cursor: !selectedFile || isBusy ? 'not-allowed' : 'pointer',
                                transition: 'background 0.18s',
                                letterSpacing: '0.04em',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                            }}
                            onMouseEnter={(e) => { if (selectedFile && !isBusy) e.currentTarget.style.background = '#3a5fcf'; }}
                            onMouseLeave={(e) => { if (selectedFile && !isBusy) e.currentTarget.style.background = '#4e73df'; }}
                        >
                            {isProcessing && <div style={{ ...SPINNER_STYLE, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff' }} />}
                            {isProcessing ? '분석 중...' : '분석 시작'}
                        </button>

                        {/* 도움말 */}
                        {!selectedFile && (
                            <div style={{ background: '#f8faff', border: '1px solid #dbeafe', borderRadius: '8px', padding: '14px 16px' }}>
                                <p style={{ fontSize: '12px', color: '#475569', margin: 0, lineHeight: 1.7 }}>
                                    <strong style={{ color: '#1e40af' }}>이미지 선택 방법</strong><br />
                                    • 왼쪽 영역에 이미지를 드래그하거나<br />
                                    • 파일 선택 버튼으로 이미지를 고르거나<br />
                                    • 모바일에서 카메라로 바로 촬영하세요
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* hidden inputs */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onFileInputChange}
                />
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={onFileInputChange}
                />
            </section>

            <style>{`@keyframes upload-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
